import { FlatLight, ShipType } from "./ShipLights.data";

function shuffle<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function flattenData(data: ShipType[]): FlatLight[] {
  return data.flatMap((ship) =>
    ship.lightConfigurations.map((light, index) => ({
      id: `${ship.type}-${index}`,
      shipType: ship.type,
      shipLabel: ship.label,
      angle: light.angle,
      imgSrc: light.imgSrc,
      description: light.description,
    }))
  );
}

function takeUniqueById(
  items: FlatLight[],
  count: number,
  excludeIds = new Set<string>()
): FlatLight[] {
  const result: FlatLight[] = [];

  for (const item of shuffle(items)) {
    if (excludeIds.has(item.id)) continue;
    if (result.some((r) => r.id === item.id)) continue;

    result.push(item);
    if (result.length >= count) break;
  }

  return result;
}

function getDistractors(
  correct: FlatLight,
  allLights: FlatLight[],
  excludeIds = new Set<string>()
): FlatLight[] {
  const others = allLights.filter(
    (item) => item.id !== correct.id && !excludeIds.has(item.id)
  );

  const sameShipType = others.filter((item) => item.shipType === correct.shipType);
  const otherShipTypes = others.filter((item) => item.shipType !== correct.shipType);

  const sameTypeCount = Math.min(
    sameShipType.length,
    Math.random() < 0.5 ? 1 : 2
  );

  const chosenSameType = takeUniqueById(sameShipType, sameTypeCount, excludeIds);

  const usedIds = new Set([
    ...excludeIds,
    ...chosenSameType.map((item) => item.id),
  ]);

  const chosenRandomOthers = takeUniqueById(
    otherShipTypes,
    3 - chosenSameType.length,
    usedIds
  );

  return [...chosenSameType, ...chosenRandomOthers];
}

export type QuizOption = {
  id: string;
  shipType: string;
  shipLabel: string;
  description: string;
};

export type QuizQuestion = {
  id: string;
  imgSrc: string;
  correctAnswerId: string;
  options: QuizOption[];
};

export function generateQuiz(
  data: ShipType[],
  amount: number = 20
): QuizQuestion[] {
  const allLights = flattenData(data);

  if (allLights.length < 4) {
    throw new Error("You need at least 4 light configurations.");
  }

  const maxQuestions = Math.min(amount, Math.floor(allLights.length / 4));
  const availableQuestions = shuffle(allLights);

  const usedAnswerIds = new Set<string>();
  const questions: QuizQuestion[] = [];

  for (const correct of availableQuestions) {
    if (questions.length >= maxQuestions) break;
    if (usedAnswerIds.has(correct.id)) continue;

    const distractors = getDistractors(correct, allLights, usedAnswerIds);

    if (distractors.length < 3) {
      continue;
    }

    const options: QuizOption[] = shuffle([
      {
        id: correct.id,
        shipType: correct.shipType,
        shipLabel: correct.shipLabel,
        description: correct.description,
      },
      ...distractors.map((item) => ({
        id: item.id,
        shipType: item.shipType,
        shipLabel: item.shipLabel,
        description: item.description,
      })),
    ]);

    questions.push({
      id: `question-${questions.length + 1}`,
      imgSrc: correct.imgSrc,
      correctAnswerId: correct.id,
      options,
    });

    usedAnswerIds.add(correct.id);
    distractors.forEach((item) => usedAnswerIds.add(item.id));
  }

  return questions;
}